// src/Components/Chat/ChatInput.jsx
import React, { useState } from "react";
import { chatService } from "../../Services/chatServices";

const ChatInput = ({
  roomDocId,
  currentPhase,
  currentUserId,
  playerName,
  disabled = false,
  onMessageSent = () => {}, // Callback for when message is sent
}) => {
  const [message, setMessage] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState("");

  const CHARACTER_LIMIT = chatService.CHARACTER_LIMIT;

  const handleInputChange = (e) => {
    const value = e.target.value;
    setMessage(value);
    setError(""); // Clear error when user types
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleSendMessage = async () => {
    // Debug: Let's see what we actually have
    console.log("Debug - Chat Input Props:", {
      currentUserId,
      playerName,
      roomDocId,
      currentPhase,
      message: message.substring(0, 50), // First 50 chars of message
    });

    // Validate inputs first with detailed logging
    if (!currentUserId) {
      console.error("Missing currentUserId");
      setError("Missing user ID - please refresh the page");
      return;
    }

    if (!playerName) {
      console.error("Missing playerName");
      setError("Missing player name - please refresh the page");
      return;
    }

    if (!roomDocId) {
      console.error("Missing roomDocId");
      setError("Missing room ID - please refresh the page");
      return;
    }

    if (!currentPhase) {
      console.error("Missing currentPhase");
      setError("Missing game phase - please refresh the page");
      return;
    }

    // Validate before sending
    const validation = chatService.validateMessage(message);

    if (!validation.isValid) {
      setError(validation.errors[0]); // Show first error
      return;
    }

    if (isSending || disabled) {
      return;
    }

    setIsSending(true);
    setError("");

    try {
      const messageData = {
        userId: currentUserId,
        playerName: playerName,
        content: message,
        type: "chat",
      };

      console.log("Sending message:", { roomDocId, currentPhase, messageData }); // Debug log

      await chatService.addMessage(roomDocId, currentPhase, messageData);

      // Clear input on successful send
      setMessage("");
      onMessageSent(); // Notify parent component
    } catch (error) {
      console.error("Error sending message:", error);
      setError(`Failed to send message: ${error.message}`);
    } finally {
      setIsSending(false);
    }
  };

  const remainingChars = CHARACTER_LIMIT - message.length;
  const isOverLimit = remainingChars < 0;
  const isNearLimit = remainingChars <= 20;

  return (
    <div className="border-t border-gray-200 bg-white p-3 md:p-4">
      {/* Error display */}
      {error && (
        <div className="mb-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">
          {error}
        </div>
      )}

      {/* Input area */}
      <div className="flex items-center space-x-2 ">
        <div className="flex-1 min-w-0">
          {/* Text input */}
          <textarea
            value={message}
            onChange={handleInputChange}
            onKeyDown={handleKeyPress}
            placeholder={
              disabled ? "Chat disabled during gameplay" : "Type a message..."
            }
            disabled={disabled || isSending}
            maxLength={CHARACTER_LIMIT}
            rows={1}
            className={`w-full px-3 py-2 border rounded-lg resize-none focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none transition-all text-sm md:text-base ${
              disabled
                ? "bg-gray-100 text-gray-500 cursor-not-allowed"
                : isOverLimit
                ? "border-red-400 bg-red-50"
                : "border-gray-300"
            }`}
            style={{
              minHeight: "36px",
              maxHeight: "80px",
            }}
            onInput={(e) => {
              // Auto-resize textarea
              e.target.style.height = "36px";
              e.target.style.height =
                Math.min(e.target.scrollHeight, 80) + "px";
            }}
          />
        </div>

        {/* Send button */}
        <button
          onClick={handleSendMessage}
          disabled={disabled || isSending || !message.trim() || isOverLimit}
          className={`px-3 py-2 md:px-4 rounded-lg font-medium transition-all text-sm whitespace-nowrap ${
            disabled || isSending || !message.trim() || isOverLimit
              ? "bg-gray-300 text-gray-500 cursor-not-allowed"
              : "bg-red-600 text-white hover:bg-red-700 focus:ring-2 focus:ring-red-500"
          }`}
          style={{ minHeight: "36px", minWidth: "60px" }}
        >
          {isSending ? (
            <div className="flex items-center justify-center">
              <svg
                className="animate-spin h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
            </div>
          ) : (
            "Send"
          )}
        </button>
      </div>
      {/* Character counter */}
      <div
        className={`text-xs mt-1 text-right ${
          isOverLimit
            ? "text-red-600 font-semibold"
            : isNearLimit
            ? "text-orange-600"
            : "text-gray-500"
        }`}
      >
        {remainingChars} characters remaining
      </div>
      {/* Helpful hints */}
      {/* {!disabled && (
        <div className="text-xs text-gray-500 mt-2 hidden md:block">
          Press Enter to send, Shift+Enter for new line
        </div>
      )} */}
    </div>
  );
};

export default ChatInput;
